import { flattenFlat } from './flattenFlat'
import type { FlatInputRow } from './types'

export type WalkSlice = {
  timeframe: string
  arm?: 'metric' | 'driver'
  rows: FlatInputRow[]
  /** FU-03: driver slices only — the fact-valid filters actually fired (`effective`) and the
   *  frozen keys the driver fact cannot carry (`dropped`), present only when they differ from
   *  the frozen filters. pinToSheet renders the self-describe metadata row from these; the
   *  flattener ignores them. */
  effective?: Record<string, string[]>
  dropped?: string[]
}

/** Flatten a multi-walk pin (metric/driver fan-out or per-timeframe overlay) into ONE flat
 *  table: a leading `Timeframe` column always, an `Arm` column only when any slice has an arm.
 *  Reuses flattenFlat per slice so dims-expansion + friendly values apply uniformly. Dim columns
 *  are unioned across slices in first-seen order and always sit between the head columns and the
 *  trailing `Value type` / `Value` measures, even when slices carry heterogeneous dim keysets. */
export function flattenMultiWalk(
  slices: WalkSlice[],
  opts: { hasTime: boolean; resolveValue?: (d: string, v: string) => string },
): { columns: string[]; rows: Array<Record<string, unknown>> } {
  const hasArm = slices.some((s) => s.arm !== undefined)
  const dimKeys: string[] = []
  const rows: Array<Record<string, unknown>> = []
  for (const s of slices) {
    const extraCols: Record<string, unknown> = { Timeframe: s.timeframe }
    if (hasArm) extraCols.Arm = s.arm ?? ''
    const flat = flattenFlat(s.rows, {
      hasTime: opts.hasTime,
      resolveValue: opts.resolveValue,
      extraCols,
    })
    for (const r of s.rows) {
      if (r.dims) for (const k of Object.keys(r.dims)) if (!dimKeys.includes(k)) dimKeys.push(k)
    }
    rows.push(...flat.rows)
  }
  const columns = [
    'Timeframe',
    ...(hasArm ? ['Arm'] : []),
    ...(opts.hasTime ? ['Time'] : []),
    ...dimKeys,
    'Value type',
    'Value',
  ]
  return { columns, rows }
}
